import { AppInjector } from '../utils/services/set-app-injector';
import { ProcessTypeEnum } from './cartDetails/process-type.enum';
import { ServiceTypeEnum } from './cartDetails/service-type.enum';
import { TrafficTypeEnum } from './cartDetails/trafficType.model';
import { OfferLegalInfoListPositionEnum } from './offer-legal-info-list-position.enum';
import { DeviceModel } from './device.model';
import { RechargingDeclination } from '../utils/pipes/rechargingDeclination';
import { MonthForm } from '../utils/pipes/monthForm';
import Utils from '../utils/utils';
import { GroupMembershipTypeEnum } from './cartDetails/groupMembershipType.model';
import { CommonInfoService } from '../cart-process/common-info.service';
import { PromotionTypeEnum } from './cartDetails/promotionType.model';
import { SimCardTypeEnum } from './cartDetails/simCardType.model';
import {
  NotificationProviderService,
  NotificationProviderTypeEnum
} from '../external-interfaces/notification-provider.service';
import { BundleTypeEnum } from './cartDetails/bundle-type.enum';
import { CommonModalService } from '../utils/modals/common-modal.service';
import { IAddons, IBanner, IBundle, IPrice, IPriceCycle, IPriceGroup } from '../interfaces/cart-details.interface';
import { PriceFormatPipe } from '../utils/pipes/price-format.pipe';
import ManageTvService from '../customer-offer/manage-tv/manage-tv.service';
import { SlotTypeEnum } from './cartDetails/slot-type.enum';
import { OfferLegalInfoList } from '../interfaces/cart-details.interface';

export default class SlotModel {

  protected commonInfoService: CommonInfoService = AppInjector.get(CommonInfoService);
  protected msisdn: string;
  protected bundleMessages: string[];
  protected monthFormDeclination: MonthForm;
  protected rechargingDeclination: RechargingDeclination;
  protected commonModalService: any = AppInjector.get(CommonModalService);

  public manageTv: ManageTvService = AppInjector.get(ManageTvService);
  public uid: string;
  public device: DeviceModel;
  public hasDevice: boolean;
  public hasGadget: boolean;
  public offerId: string;
  public bundleType: string;
  public bundleNumber: number;
  public termOfServiceNumber: number;
  public deviceTermLength: number;
  public oldContractEndingDate: number;
  public slotNo: number;
  public leafletPrice: number;
  public trafficType: TrafficTypeEnum;
  public processType: ProcessTypeEnum;
  public serviceType: ServiceTypeEnum;
  public promotionType: PromotionTypeEnum;
  public promotionName: string;
  public addons: IAddons;
  public selectedAddonsPrice: IPriceCycle[] | undefined;
  public gadgets: any;
  public offerName: string;
  public title: string;
  public summaryTitle: string;
  public isOwner: boolean;
  public isOneTimePaymentVisible: boolean;
  public simCardType: SimCardTypeEnum;
  public oneTimePaymentLabel: string;
  public possibleChangeDevice: boolean;
  public hasNoHandsetOffer: boolean; // znaczenie na odwrot - false w przypadku oferty bez urzadzenia, true z urzadzeniem
  public discountInformation: string;
  public banners: IBanner[];
  public offerChangePrices: IPriceGroup;
  public newContractPrices: IPriceGroup;
  public slotTypeEnum = SlotTypeEnum;
  public offerLegalInfoList: OfferLegalInfoList[];

  private notificationProvider2: NotificationProviderService = AppInjector.get(NotificationProviderService);

  // for compatibility with previous version
  public _computedFields: any = {
    selectedAddons: <any>[],
    gadgetsList: <any>[]
  };

  constructor(
    private bundle: IBundle,
    public ordinal: number,
    protected ordinalNumberInGroup: number
  ) {
    this.uid = this.bundle.uid;
    this.isOwner = this.bundle.offer.groupMembership === GroupMembershipTypeEnum.OWNER;
    this.bundleType = this.bundle.bundleType;
    this.bundleNumber = this.bundle.number;
    this.msisdn = this.bundle.offer.msisdn.number;
    this.device = this.bundle.device[0] ? new DeviceModel(this.bundle.device[0], this.commonInfoService) : null;
    this.hasDevice = !!this.bundle.device.length;
    this.hasGadget = !!this.bundle.gadget.length;
    this.offerId = this.bundle.offer.offerId;
    this.termOfServiceNumber = this.bundle.offer.termOfServiceNumber || 0;
    this.deviceTermLength = this.bundle.offer.deviceTermLength || 0;
    this.oldContractEndingDate = this.bundle.offer.oldContractEndingDate;
    this.leafletPrice = this.bundle.offer.leafletPrice;
    this.slotNo = this.bundle.slotNo;
    this.addons = this.bundle.addons;
    this.gadgets = this.bundle.gadget || [];
    this.offerName = this.bundle.offer.offerName;
    this.bundleMessages = this.bundle.messages.messages;
    this.trafficType = this.bundle.offer.trafficType;
    this.processType = this.bundle.processType;
    this.serviceType = this.bundle.offer.serviceType;
    this.promotionType = this.bundle.offer.promotionType;
    this.promotionName = this.bundle.offer.promotionName;
    this.monthFormDeclination = new MonthForm();
    this.rechargingDeclination = new RechargingDeclination();
    this.simCardType = this.bundle.offer.simCardType;
    this.isOneTimePaymentVisible = this.hasDevice
      && ((this.isTrafficTypeTvOrData()
      && !this.isLeasing()
      && parseFloat(this.getDevicePrice()) > 0) || !this.isTrafficTypeTvOrData());
    this.oneTimePaymentLabel = this.getOneTimePaymentLabel();
    this.possibleChangeDevice = this.bundle.offer.possibleChangeDevice;
    this.hasNoHandsetOffer = this.bundle.offer.hasNoHandsetOffer;
    this.discountInformation = this.bundle.offer.discountInformation;
    this.banners = this.bundle.banners && this.bundle.banners.length ? this.bundle.banners : [];
    this.offerChangePrices = this.bundle.offer.offerChangePrices;
    this.newContractPrices = this.bundle.offer.newContractPrices;
    this.offerLegalInfoList = this.bundle.offer.offerLegalInfoList;

    this.notificationProvider2.subscribe(
      NotificationProviderTypeEnum.COMMON_INFO,
      (commonInfo: any): void => {
        if (!commonInfo) {
          return;
        }

        this.setSlotTitle();
      }
    );
  }

  public getMsisdn(): string {
    if (this.trafficType === TrafficTypeEnum.TV) {
      return this.manageTv.msisdn;
    }

    return this.msisdn;
  }

  public setMsisdn(msisdn: string): void {
    this.msisdn = msisdn;
  }

  public getPrice(price: IPriceCycle | IPrice): string | undefined {
    return price ? price[this.commonInfoService.taxName] : undefined;
  }

  public getMainPrice(priceList: IPriceCycle[]): string | undefined {
    return this.getPrice(priceList[0].price);
  }

  public getOldPrice(priceList: IPriceCycle[]): string | undefined {
    return this.getPrice(priceList[0].oldPrice);
  }

  public getDevicePrice(): string {
    return this.getPrice(this.device.price);
  }

  public isLeasing(): boolean {
    return this.promotionType === PromotionTypeEnum.LEASING;
  }

  public isM2mCard(): boolean {
    return this.simCardType === SimCardTypeEnum.M2M;
  }

  public get isInstallmentFlex(): boolean {
    return this.promotionType === PromotionTypeEnum.INSTALLMENT_FLEX;
  }

  public isTrafficTypeTV(): boolean {
    return this.trafficType === TrafficTypeEnum.TV;
  }

  public isTrafficTypeVoice(): boolean {
    return this.trafficType === TrafficTypeEnum.VOICE;
  }

  public isTrafficTypeTvOrData(): boolean {
    return this.isTrafficTypeTV() || this.trafficType === TrafficTypeEnum.DATA;
  }

  public getTooltipContent(): string {
    if (this.discountInformation) {
      let text: string = this.discountInformation;

      if (!this.commonInfoService.isBohoOffer
          && this.commonInfoService.simCardDiscountApplied === true
          && this.commonInfoService?.simCardDiscount && this.ordinal === 1) {
        const price = this.commonInfoService.simCardDiscount[this.commonInfoService.taxName];
        const priceFormat: PriceFormatPipe = new PriceFormatPipe();

        text += ` oraz rabat ${priceFormat.transform(price) + ' ' + this.commonInfoService.priceEnding} za przedłużenie minimum 5 numerów`;
      }

      return text;
    }

    return null;
  }

  public getOfferLegalInfo(slotType?: SlotTypeEnum): OfferLegalInfoList | OfferLegalInfoList[] | '' {
    const subscriptionContent: OfferLegalInfoList = this.offerLegalInfoList?.find(
      (content: OfferLegalInfoList): boolean => content.position === OfferLegalInfoListPositionEnum.SUBSCRIPTION);
    const installmentContent: OfferLegalInfoList = this.offerLegalInfoList?.find(
      (content: OfferLegalInfoList): boolean => content.position === OfferLegalInfoListPositionEnum.INSTALLMENT);

    if (slotType === SlotTypeEnum.MEMBER) {
      if (this.serviceType === ServiceTypeEnum.MIX) {
        return '';
      }

      return subscriptionContent;
    }

    if (slotType === SlotTypeEnum.DEVICE) {
      if (this.serviceType === ServiceTypeEnum.MIX) {
        return '';
      }
      return installmentContent;
    }

    if (this.serviceType === ServiceTypeEnum.MIX) {
      this.offerLegalInfoList = [];
    }

    return this.offerLegalInfoList;
  }

  private getOneTimePaymentLabel(): string {
    return this.isInstallmentFlex ? 'opłata na start' : 'jednorazowo';
  }

  private setSlotTitle(): void {
    let slotTitle = '';
    let summaryTitle = '';

    if ((this.serviceType === ServiceTypeEnum.POSTPAID
      || this.serviceType === ServiceTypeEnum.MIX)
      && this.trafficType === TrafficTypeEnum.VOICE
      && this.commonInfoService.isSolo
    ) {
      slotTitle = 'Numer Telefonu';
      summaryTitle = 'Twój numer';
    } else if ((this.serviceType === ServiceTypeEnum.POSTPAID
      || this.serviceType === ServiceTypeEnum.MIX)
      && this.trafficType === TrafficTypeEnum.DATA
      && this.commonInfoService.isSolo
    ) {
      slotTitle = 'Internet';
      summaryTitle = 'Internet bezprzewodowy';
    } else if (this.trafficType === TrafficTypeEnum.TV) {
      slotTitle = summaryTitle = 'Telewizja';
    }  else if (!this.ordinalNumberInGroup || this.bundleType === BundleTypeEnum.ADDITIONAL_OFFER) {
      slotTitle = summaryTitle = '';
    } else {
      // Np. Czwarty numer
      slotTitle = summaryTitle = Utils.numberDeclensionFormat(this.ordinalNumberInGroup);
    }

    if (this.isOwner && !this.commonInfoService.isVirginBrand && !this.commonInfoService.isNextSim) {
      // Np. Pierwszy numer - właściciel grupy
      slotTitle += ' - właściciel grupy';
    }

    this.title = slotTitle;
    this.summaryTitle = summaryTitle;
  }
}
